import { useEffect, useRef, useState } from 'react';

import { getUserData } from '../../../constants/global';
import { useGlobalContext } from '../../../context/global';
import type { IActions } from '../../../interface/IAction';
import type { IStates } from '../../../interface/IReducer';
import { type IGetElectionOfficial } from '../../../interface/state/IElectionState';
import type { ILGAInState } from '../../../interface/state/ILGAState';
import type { IPartyState } from '../../../interface/state/IParty';
import type { IPollingUnitInWardsState } from '../../../interface/state/IPollingUnitState';
import type { IWardInLGAState } from '../../../interface/state/IWardState';
import { resolvePartyList } from '../../../lib/ballot-parties-api';
import { resultApiVersion } from '../../../lib/result-submission-api';
import type {
	ICreateLGAResult,
	ICreatePUResult,
	ICreatePUResultV2,
	ICreateStateResult,
	ICreateWardResult,
	ILGAResultMap,
	IResult,
	IStateResultMap,
	IWardResultMap,
} from '../../../store/actions/query/irev';
import type { IComponentState } from '../../layout/component-state';
import { type IUSH, useStateHook } from '../../layout/state-hook';
import { buildPuResultV1, buildPuResultV2, type IPuPayloadInput, missingV2Fields } from './pu-payload';
import {
	defaultPartyBallot,
	defaultSummary,
	type IChildCode,
	type IPartyVotes,
	type IVoteCount,
	reconcileVoteRows,
	seedVoteRows,
} from './utils';

export const getResults = (states: IStates | undefined, partyVotes: IPartyVotes[]): IResult[] => {
	const allParties = states?._party.get_Party?.data;

	const mergedOutput: IResult[] = partyVotes
		? Array.from(
				partyVotes
					?.flatMap?.((item) =>
						item.votes.map((i) => ({
							...i,
							votes: parseInt(i.votes + '') || 0,
						}))
					)
					?.reduce((acc, curr) => {
						if (acc.has(curr.partyId)) {
							acc.get(curr.partyId)!.votes += parseInt(curr.votes + '');
						} else {
							acc.set(curr.partyId, {
								...curr,
								partyName: curr.partyName || allParties?.filter((j) => j.id === curr.partyId)?.[0]?.shortName || '',
							});
						}
						return acc;
					}, new Map<number, IResult>())
					.values()
			)
		: [];
	return mergedOutput;
};

const getZoneProps = (states: IStates | undefined) => {
	const zoneProps = states?._geographicalzone?.get_GeographicalZone?.data || [];

	const geoZoneId = states?._countryState?.get_CountryStateByID?.data?.geographicalZoneId || 0;
	return zoneProps.filter((i) => i.id + '' === geoZoneId + '')?.[0];
};

export const useStateResultDataManager = ({ results }: { results: IResult[] }): { data: ICreateStateResult } => {
	const {
		states,
		global: {
			state: { resultSummary, uploadedFiles, partyVotes, electionData, selectedParentCode },
		},
	} = useGlobalContext();
	const geoZoneProps = getZoneProps(states);
	const getStateResultsMap = () => {
		const transformedArray: IStateResultMap[] = partyVotes.flatMap((item) =>
			item.votes.map(({ partyId, votes, partyName }: IVoteCount) => ({
				lgaId: item.id,
				lgaCode: item.code,
				partyName,
				partyId,
				votes: parseInt(votes + '') || 0,
			}))
		);
		return transformedArray;
	};
	const data: ICreateStateResult = {
		accreditedVoters: resultSummary.DataAccreditedVoters,
		ballotPapersIssuedToPoolingUnit: resultSummary.DataBallotPapersIssuedToPoolingUnit,
		totalUsedBallotPapers: resultSummary.DataTotalUsedBallotPapers,
		totalValidVotes: resultSummary.DataTotalValidVotes,
		unusedBallotPapers: resultSummary.DataUnusedBallotPapers,
		votersOnRegister: resultSummary.DataVotersOnRegister,
		rejectedBallot: resultSummary.DataRejectedBallot,
		documentUrls: uploadedFiles,
		resultSource: 2,
		results: results.map((i) => ({
			partyId: i.partyId,
			partyName: i.partyName,
			votes: i.votes,
		})),
		resultMap: getStateResultsMap(),
		createdBy: {
			id: parseInt(getUserData().user?.UserId || ''),
			name: getUserData().user?.FullName || '',
		},
		election: {
			id: electionData?.data?.[0]?.electionId || 0,
			name: electionData?.data?.[0]?.election || '',
		},
		localGovernment: {
			code: selectedParentCode?.code,
			name: selectedParentCode.name,
		},
		presidingOfficer: {
			id: getUserData().user?.UserId || '',
			name: getUserData().user?.FullName || '',
		},
		state: {
			code: states?._countryState?.get_CountryStateByID?.data?.stateCode + '',
			name: states?._countryState?.get_CountryStateByID?.data?.name + '',
		},
		geoZone: {
			code: geoZoneProps?.code,
			name: geoZoneProps?.name,
		},
		zone: {
			code: geoZoneProps?.code,
			name: geoZoneProps?.zone?.name,
		},
		flags: [],
	};
	return {
		data,
	};
};

export const useLGAResultDataManager = ({ results }: { results: IResult[] }): { data: ICreateLGAResult } => {
	const {
		states,
		global: {
			state: { resultSummary, uploadedFiles, partyVotes, electionData, selectedParentCode },
		},
	} = useGlobalContext();

	const getLGAResultsMap = () => {
		const transformedArray: ILGAResultMap[] = partyVotes.flatMap((item) =>
			item.votes.map(({ partyId, votes, partyName }: IVoteCount) => ({
				wardId: item.id,
				wardCode: item.code,
				partyName,
				partyId,
				votes: parseInt(votes + '') || 0,
			}))
		);
		return transformedArray;
	};
	const geoZoneProps = getZoneProps(states);
	const data: ICreateLGAResult = {
		accreditedVoters: resultSummary.DataAccreditedVoters,
		resultSource: 2,
		ballotPapersIssuedToPoolingUnit: resultSummary.DataBallotPapersIssuedToPoolingUnit,
		totalUsedBallotPapers: resultSummary.DataTotalUsedBallotPapers,
		totalValidVotes: resultSummary.DataTotalValidVotes,
		unusedBallotPapers: resultSummary.DataUnusedBallotPapers,
		votersOnRegister: resultSummary.DataVotersOnRegister,
		rejectedBallot: resultSummary.DataRejectedBallot,
		documentUrls: uploadedFiles,
		results: results.map((i) => ({
			partyId: i.partyId,
			partyName: i.partyName,
			votes: i.votes,
		})),
		resultMap: getLGAResultsMap(),
		createdBy: {
			id: parseInt(getUserData().user?.UserId || ''),
			name: getUserData().user?.FullName || '',
		},
		election: {
			id: electionData?.data?.[0]?.electionId || 0,
			name: electionData?.data?.[0]?.election || '',
		},
		localGovernment: {
			code: selectedParentCode?.code,
			name: selectedParentCode.name,
		},
		presidingOfficer: {
			id: getUserData().user?.UserId || '',
			name: getUserData().user?.FullName || '',
		},
		state: {
			code: states?._countryState?.get_CountryStateByID?.data?.stateCode + '',
			name: states?._countryState?.get_CountryStateByID?.data?.name + '',
		},
		geoZone: {
			code: geoZoneProps?.code,
			name: geoZoneProps?.name,
		},
		zone: {
			code: geoZoneProps?.code,
			name: geoZoneProps?.zone?.name,
		},
	};
	return {
		data,
	};
};

export const useWardResultDataManager = ({ results }: { results: IResult[] }): { data: ICreateWardResult } => {
	const {
		states,
		global: {
			state: { resultSummary, uploadedFiles, partyVotes, selectedParentCode, electionData },
		},
	} = useGlobalContext();
	const geoZoneProps = getZoneProps(states);
	const getWardResultsMap = () => {
		const transformedArray: IWardResultMap[] = partyVotes.flatMap((item) =>
			item.votes.map(({ partyId, votes, partyName }: IVoteCount) => ({
				pollingUnitId: item.id,
				pollingUnitName: item.name,
				pollingUnitCode: item.code,
				partyName,
				partyId,
				votes: parseInt(votes + '') || 0,
			}))
		);
		return transformedArray;
	};
	const lga = states?._ward?.get_WardByCode?.data?.[0]?.lga;
	const data: ICreateWardResult = {
		accreditedVoters: resultSummary.DataAccreditedVoters,
		resultSource: 2,
		ballotPapersIssuedToPoolingUnit: resultSummary.DataBallotPapersIssuedToPoolingUnit,
		totalUsedBallotPapers: resultSummary.DataTotalUsedBallotPapers,
		totalValidVotes: resultSummary.DataTotalValidVotes,
		unusedBallotPapers: resultSummary.DataUnusedBallotPapers,
		votersOnRegister: resultSummary.DataVotersOnRegister,
		rejectedBallot: resultSummary.DataRejectedBallot,
		documentUrls: uploadedFiles,
		results: results.map((i) => ({
			partyId: i.partyId,
			partyName: i.partyName,
			votes: i.votes,
		})),
		resultMap: getWardResultsMap(),
		createdBy: {
			id: parseInt(getUserData().user?.UserId || ''),
			name: getUserData().user?.FullName || '',
		},
		election: {
			id: electionData?.data?.[0]?.electionId || 0,
			name: electionData?.data?.[0]?.election || '',
		},
		ward: {
			code: selectedParentCode?.code,
			name: selectedParentCode.name,
		},
		presidingOfficer: {
			id: getUserData().user?.UserId || '',
			name: getUserData().user?.FullName || '',
		},
		state: {
			code: states?._countryState?.get_CountryStateByID?.data?.stateCode + '',
			name: states?._countryState?.get_CountryStateByID?.data?.name + '',
		},
		geoZone: {
			code: geoZoneProps?.code,
			name: geoZoneProps?.name,
		},
		zone: {
			code: geoZoneProps?.code,
			name: geoZoneProps?.zone?.name,
		},
		flags: [],
		localGovernment: {
			code: lga?.lgaCode || '',
			name: lga?.name || '',
		},
	};
	return {
		data,
	};
};

/**
 * Thin wrapper over the pure builders in `pu-payload.ts`.
 *
 * Which shape it returns is decided by the per-level version flag, so a revert to V1 is a
 * one-line change in `src/lib/result-submission-api.ts` and not a code change here.
 * `missing` names the parts of a V2 payload that a background lookup has not filled in yet;
 * the Preview stage renders it and blocks submit, because the server accepts a blank division
 * object and turns it into a saga row with an empty code.
 */
export const usePollingUnitResultDataManager = ({
	results,
}: {
	results: IResult[];
}): { data: ICreatePUResult | ICreatePUResultV2; missing: string[] } => {
	const {
		states,
		global: {
			state: { resultSummary, uploadedFiles, electionData, selectedParentCode },
		},
	} = useGlobalContext();

	const input: IPuPayloadInput = {
		states,
		global: { resultSummary, uploadedFiles, electionData, selectedParentCode },
		results,
		user: {
			id: getUserData().user?.UserId || '',
			name: getUserData().user?.FullName || '',
		},
	};

	if (resultApiVersion('EC8A') === 'v1') return { data: buildPuResultV1(input), missing: [] };

	const data = buildPuResultV2(input);
	return { data, missing: missingV2Fields(data) };
};

export interface IUseCodeProps {
	handleCodes: () => void;
	load: boolean;
}

export const useGetCodes = ({
	actions,
	states,
	global,
}: {
	actions: IActions | undefined;
	states: IStates | undefined;
	global: IUSH<IComponentState>;
}): IUseCodeProps => {
	const {
		updateState,
		state: { childCodes, selectedParentCode, resultType, partyVotes, electionData },
	} = global;

	const [ballotLoading, setBallotLoading] = useState(false);
	// The resolver call has no cancel token, so a response for an entity the agent has
	// already left must be dropped rather than seed the wrong children.
	const seedSeqRef = useRef(0);
	// The party seed lands after an async resolver call, so it must merge into the votes as
	// they are then, not as they were when Refresh was pressed.
	const partyVotesRef = useRef(partyVotes);
	partyVotesRef.current = partyVotes;

	// The parties on this election's ballot when the resolver knows them; otherwise the
	// existing all-parties list (no election id yet, or the resolver isn't deployed).
	const seedPartyList = (onParties: (parties: IPartyState[]) => void) => {
		// The ballot rides beside partyVotes (never on the rows) so the payload is untouched.
		seedSeqRef.current += 1;
		const seq = seedSeqRef.current;
		const electionId = Number(electionData?.data?.[0]?.electionId) || 0;
		if (electionId) setBallotLoading(true);
		void resolvePartyList({
			electionId,
			onBallot: (source, parties) => {
				if (seq !== seedSeqRef.current) return;
				updateState('partyBallot', {
					source,
					order: Object.fromEntries(parties.map((p) => [String(p.id), p.ballotOrder])),
				});
				onParties(parties);
			},
			onAllParties: (source) => {
				if (seq !== seedSeqRef.current) return;
				actions?.get_Party({
					onSuccess: (res) => {
						updateState('partyBallot', { ...defaultPartyBallot, source });
						onParties(res.data);
					},
				});
			},
		}).finally(() => {
			if (seq === seedSeqRef.current) setBallotLoading(false);
		});
	};

	const getPartyVotes = (id: number, name: string, code: string, parties: IPartyState[]): IPartyVotes => {
		const pv = partyVotesRef.current.filter((i) => i.id === id)[0];
		if (!pv)
			return {
				id,
				votes: seedVoteRows(parties),
				name,
				code,
			};
		// An entity seeded earlier (a narrower ballot, a restored draft) must take the
		// current list, or the agent is stuck on the old parties even after Refresh.
		const votes = reconcileVoteRows(pv.votes, parties);
		return votes === pv.votes ? pv : { ...pv, votes };
	};

	type ccDataType = IPollingUnitInWardsState | IWardInLGAState | ILGAInState;

	// True when child codes are unseeded/mismatched OR any of these children is
	// missing its party votes seed (e.g. childCodes restored from a draft while
	// partyVotes is empty) — in either case the party seed must run. Seeding is
	// idempotent: getPartyVotes/getChildCodes preserve existing entries by id.
	const needsPartySeed = (data: { id: number }[]) =>
		!childCodes.length || childCodes.length !== data.length || !data.every((i) => partyVotes.some((pv) => pv.id === i.id));

	const getChildCodes = (data: ccDataType, code: string, parent: number): IChildCode => {
		const cc = childCodes.filter((i) => i.codeId === data.id)[0];
		if (!cc)
			return {
				code,
				parent,
				codeId: data.id,
				name: data.name,
				status: false,
			};
		return cc;
	};

	const handleDetail = () => {
		const id = [selectedParentCode.codeId];

		if (resultType === 'EC8A') {
			const seedParties = () =>
				seedPartyList((parties) => {
					updateState('partyVotes', [
						{
							...getPartyVotes(Number(selectedParentCode?.codeId), selectedParentCode.name, selectedParentCode.code, parties),
						},
					]);
				});

			// The V2 payload requires this polling unit's ward and LGA, which nothing else in the
			// EC8A flow loads. One call supplies both. Sequenced rather than parallel to match the
			// discipline used elsewhere in this file, and the party seed runs either way — a geo
			// lookup failure must not cost the agent their vote-entry screen. The Preview gate
			// (missingV2Fields) is what stops a submit with the lookup still missing.
			actions?.get_PoolingUnitByCode({
				poolingUnitCode: selectedParentCode.code,
				onSuccess: seedParties,
				onFailure: seedParties,
			});
		}
		if (resultType === 'EC8B') {
			actions?.get_PoolingUnitInWard({
				data: {
					wardIds: id,
				},
				onSuccess: (res) => {
					if (needsPartySeed(res.data)) {
						updateState(
							'childCodes',
							res.data.map((i) => ({
								...getChildCodes(i, i.poolingUnitCode, i.wardId),
							}))
						);
					}
					// Always re-seed: Refresh must pick up a changed ballot even when every
					// child already has rows (reconcileVoteRows keeps their votes).
					seedPartyList((parties) => {
						updateState(
							'partyVotes',
							res.data.map((i) => ({
								...getPartyVotes(i.id, i.name, i.poolingUnitCode, parties),
							}))
						);
					});
				},
			});
		}
		if (resultType === 'EC8C') {
			actions?.get_WardInLGA({
				lgaIds: id,
				onSuccess: (res) => {
					if (needsPartySeed(res.data)) {
						updateState(
							'childCodes',
							res.data.map((i) => ({
								...getChildCodes(i, i.wardCode, i.lgaId),
							}))
						);
					}
					// Always re-seed: Refresh must pick up a changed ballot even when every
					// child already has rows (reconcileVoteRows keeps their votes).
					seedPartyList((parties) => {
						updateState(
							'partyVotes',
							res.data.map((i) => ({
								...getPartyVotes(i.id, i.name, i.wardCode, parties),
							}))
						);
					});
				},
			});
		}
		if (resultType === 'EC8D') {
			actions?.get_LGAInState({
				stateIds: id,
				onSuccess: (res) => {
					if (needsPartySeed(res.data)) {
						updateState(
							'childCodes',
							res.data.map((i) => ({
								...getChildCodes(i, i.lgaCode, i.stateId),
							}))
						);
					}
					// Always re-seed: Refresh must pick up a changed ballot even when every
					// child already has rows (reconcileVoteRows keeps their votes).
					seedPartyList((parties) => {
						updateState(
							'partyVotes',
							res.data.map((i) => ({
								...getPartyVotes(i.id, i.name, i.lgaCode, parties),
							}))
						);
					});
				},
			});
		}
	};

	const handleCodes = () => {
		// codeId 0 is the unselected default — seeding partyVotes under id 0
		// would orphan the data once a real parent code is selected.
		if (selectedParentCode?.codeId) {
			handleDetail();
			actions?.get_Zone({});
			actions?.get_GeographicalZone({});
		}
	};

	return {
		handleCodes,
		load:
			states?._poolingUnit?.get_PoolingUnitByCodeLoading ||
			states?._ward?.get_WardByCodeLoading ||
			states?._poolingUnit?.get_PoolingUnitInWardLoading ||
			states?._ward?.get_WardInLGALoading ||
			states?._party?.get_PartyLoading ||
			states?._lga?.get_LGAInStateLoading ||
			ballotLoading ||
			false,
	};
};

export type PartyLoadStatus = 'loading' | 'empty' | 'error' | 'ready';

export interface IUseAutoLoadParties {
	status: PartyLoadStatus;
	retry: () => void;
}

const PARTY_LOAD_MAX_ATTEMPTS = 3; // 1 initial fetch + 2 auto-retries
const PARTY_LOAD_BACKOFF_MS = [400, 1200];

/**
 * Auto-loads the party list when the Party Vote Count step opens and the
 * current entity has no seeded party votes, retrying (with backoff) when a
 * fetch cycle completes empty. Returns a render status for the party grid.
 *
 * Loop-safety (see CLAUDE.md): `handleCodes` closes over `actions` (a fresh
 * reference every render) so it's read through a ref; effects depend only on
 * `id` and the `load` transition — never on `partyVotes`/`hasParties`.
 */
export const useAutoLoadParties = ({
	id,
	hasParties,
	handleCodes,
	load,
	hasError,
}: {
	id: number;
	hasParties: boolean;
	handleCodes: () => void;
	load: boolean;
	hasError: boolean;
}): IUseAutoLoadParties => {
	const attemptsRef = useRef(0);
	const retryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const prevLoadRef = useRef(load);
	const handleCodesRef = useRef(handleCodes);
	handleCodesRef.current = handleCodes;
	const hasPartiesRef = useRef(hasParties);
	hasPartiesRef.current = hasParties;
	const [exhausted, setExhausted] = useState(false);

	const clearRetryTimer = () => {
		if (retryTimerRef.current) {
			clearTimeout(retryTimerRef.current);
			retryTimerRef.current = null;
		}
	};

	// Initial fetch on step entry / when the selected entity changes
	useEffect(() => {
		attemptsRef.current = 0;
		setExhausted(false);
		clearRetryTimer();
		// Reset the transition tracker so a load cycle already in flight for the
		// previous entity isn't misread as a completed cycle for this one.
		prevLoadRef.current = false;
		if (id && !hasPartiesRef.current) {
			attemptsRef.current = 1;
			handleCodesRef.current();
		}
		return clearRetryTimer;
	}, [id]);

	// When a fetch cycle completes (load true → false) and the list is still
	// empty, schedule a sequential retry — never parallel (cancel-token hazard).
	useEffect(() => {
		const wasLoading = prevLoadRef.current;
		prevLoadRef.current = load;
		if (!wasLoading || load) return;
		if (!id || hasPartiesRef.current) return;
		if (attemptsRef.current >= PARTY_LOAD_MAX_ATTEMPTS) {
			setExhausted(true);
			return;
		}
		const delay = PARTY_LOAD_BACKOFF_MS[attemptsRef.current - 1] ?? 1200;
		clearRetryTimer();
		retryTimerRef.current = setTimeout(() => {
			attemptsRef.current += 1;
			handleCodesRef.current();
		}, delay);
	}, [load, id]);

	const retry = () => {
		clearRetryTimer();
		attemptsRef.current = 1;
		setExhausted(false);
		handleCodesRef.current();
	};

	let status: PartyLoadStatus;
	if (hasParties) status = 'ready';
	else if (!id) status = 'empty';
	else if (load || !exhausted) status = 'loading';
	else if (hasError) status = 'error';
	else status = 'empty';

	return { status, retry };
};

export const defaultParentCode = {
	code: '',
	name: '',
	codeId: 0,
	status: false,
};

export const defaultChildCode = {
	code: '',
	name: '',
	codeId: 0,
	parent: 0,
	status: false,
};

export const defaultReportIssues = {
	userId: '',
	report: {
		title: '',
		body: '',
	},
	phoneNumber: '',
	email: '',
};

export const defaultElectionData: IGetElectionOfficial = {
	isSuccessful: false,
	message: '',
	statusCode: 0,
	data: [
		{
			id: '',
			userId: 0,
			electionId: 0,
			election: '',
			name: '',
			assignment: {
				id: 0,
				code: '',
				name: '',
				resultType: 'EC8A',
				isCompleted: false,
			},
		},
	],
};

export const initComponentState = {
	draftStatus: false,
	stage: 'Electoral Division',
	isPreview: false,
	partyVotes: [],
	partyBallot: defaultPartyBallot,
	resultSummary: defaultSummary,
	files: [],
	uploadedFiles: [],
	uploadedAt: '',
	isUpdate: false,
	selectedParentCode: defaultParentCode,
	partyVoteStage: 'Party Votes',
	partyVoteDeepLink: false,
	agentPresence: [],
	selectedChildCode: defaultChildCode,
	parentCodes: [],
	childCodes: [],
	reportIssues: defaultReportIssues,
	resultType: '',
	electionData: defaultElectionData,
	load: false,
	onExtract: false,
} as IComponentState;

interface IDraft {
	id: string;
	data: IComponentState;
}

export const useAppData = (
	actions: IActions | undefined,
	states: IStates | undefined
): {
	global: IUSH<IComponentState>;
	saveAsDraft: () => void;
	restoreDraft: (id: string) => void;
	clearDraft: (id: string) => void;
	clearDraftLocal: () => void;
	isDraft: (code: string) => boolean;
} => {
	const global = useStateHook<IComponentState>(initComponentState);

	const id = global.state.selectedParentCode?.code;

	const DRAFTID = 'APP_DRAFT';

	const getDrafts = () => {
		const draft = localStorage.getItem(DRAFTID) ?? states?._draft?.get_Draft?.data?.[0]?.token;
		const parsedDraft = draft ? (JSON.parse(draft) as IDraft[]) : [];
		return parsedDraft;
	};

	const getToken = () => {
		const currentDrafts = getDrafts() || [];
		if (!id) return null;
		let newDraft: IDraft[] = [];
		if (!currentDrafts?.map((i) => i.id).includes(id)) {
			newDraft = [...currentDrafts, { id, data: { ...global.state } }];
		} else {
			newDraft = currentDrafts.map((i) => ({
				...i,
				data: i.id === id ? { ...global.state } : i.data,
			}));
		}
		return JSON.stringify(newDraft);
	};

	const createUpdateDraft = (token: string) => {
		actions?.create_Draft({
			data: {
				token,
				userId: parseInt(getUserData().user?.UserId || ''),
				referenceId: getUserData().user?.UserId || '',
				id: getUserData().user?.UserId || '',
			},
			onSuccess: () => {
				global.updateState('draftStatus', false);
				localStorage.setItem(DRAFTID, token);
			},
			onFailure: () => {
				global.updateState('draftStatus', false);
				localStorage.setItem(DRAFTID, token);
			},
		});
	};

	const deleteDraft = () => {
		actions?.delete_Draft({
			id: states?._draft?.get_Draft?.data?.[0]?.id || '',
			onSuccess: () => {
				global.updateState('draftStatus', false);
				localStorage.removeItem(DRAFTID);
			},
			onFailure: () => {
				global.updateState('draftStatus', false);
			},
		});
	};

	const saveAsDraft = () => {
		global.updateState('draftStatus', true);
		const token = getToken();
		if (!token) {
			global.updateState('draftStatus', false);
		} else {
			createUpdateDraft(token);
		}
	};

	const updateStateWithDraft = (draft: IComponentState) => {
		// resultType: '',
		// electionData: defaultElectionData,
		// load: false

		const {
			state: {
				childCodes,
				draftStatus,
				files,
				isPreview,
				isUpdate,
				parentCodes,
				partyVoteStage,
				partyVotes,
				resultSummary,
				selectedChildCode,
				selectedParentCode,
				stage,
				uploadedFiles,
				reportIssues,
				agentPresence,
			},
		} = global;
		global.updateState('reportIssues', draft.reportIssues || reportIssues);
		global.updateState('childCodes', draft.childCodes || childCodes);
		global.updateState('parentCodes', draft.parentCodes || parentCodes);
		global.updateState('draftStatus', draft.draftStatus || draftStatus);
		global.updateState('partyVotes', draft.partyVotes || partyVotes);
		// The ballot belongs to the restored rows; drafts saved before it existed get the
		// alphabetical default rather than whatever entity was open before.
		global.updateState('partyBallot', draft.partyVotes ? draft.partyBallot || defaultPartyBallot : global.state.partyBallot);
		global.updateState('selectedChildCode', draft.selectedChildCode || selectedChildCode);
		global.updateState('stage', draft.stage || stage);
		global.updateState('isPreview', draft.isPreview || isPreview);
		global.updateState('resultSummary', draft.resultSummary || resultSummary);
		global.updateState('files', draft.files || files);
		global.updateState('uploadedFiles', draft.uploadedFiles || uploadedFiles);
		global.updateState('isUpdate', draft.isUpdate || isUpdate);
		global.updateState('selectedParentCode', draft.selectedParentCode || selectedParentCode || defaultParentCode);
		global.updateState('partyVoteStage', draft.partyVoteStage || partyVoteStage);
		// Drafts saved before this field existed carry no key at all.
		global.updateState('agentPresence', Array.isArray(draft.agentPresence) ? draft.agentPresence : agentPresence);
	};

	const isDraft = (id: string) =>
		getDrafts()
			.map((i) => i.id)
			.includes(id) || false;

	const restoreDraft = (id: string) => {
		const drafts = getDrafts();
		localStorage.setItem(DRAFTID, JSON.stringify(drafts));
		const draft: IDraft = drafts.filter((i) => i.id === id)?.[0];
		if (draft?.data) {
			updateStateWithDraft(draft.data);
		}
	};

	const clearDraftLocal = () => {
		localStorage.removeItem(DRAFTID);
	};

	const clearDraft = (id: string) => {
		global.updateState('draftStatus', true);
		const drafts = getDrafts();
		const filteredDraft = drafts.filter((i) => i.id !== id);
		if (!filteredDraft.length) {
			deleteDraft();
		} else {
			const token = JSON.stringify(filteredDraft);
			createUpdateDraft(token);
		}
	};

	return {
		global,
		saveAsDraft,
		restoreDraft,
		clearDraft,
		clearDraftLocal,
		isDraft,
	};
};
